import { useCallback, useEffect, useState } from 'react'
import { mergePublic } from '../services/store'
import { fetchPublicLanding } from '../services/landingService'
import { fetchPublicForm, fetchPublicFormById } from '../services/formService'
import { fetchPublicOrganization } from '../services/organizationService'
import { mark, measure, startPublicPerf, timed } from '../utils/perf'

// Tiempos del navegador antes de que React exista (DNS, TLS, TTFB, JS) y
// resumen de peticiones al terminar de cargar. Solo escribe en la consola.
startPublicPerf()

/**
 * Estado de carga de una página pública.
 *
 * `cargando` es el estado inicial y nunca debe mostrar «esta página no existe»:
 * el 404 solo corresponde a `no_encontrada`, es decir, cuando la consulta ya
 * terminó correctamente y no hay ninguna landing publicada con ese slug.
 * Un fallo de red (habitual al abrir el enlace desde WhatsApp en el móvil)
 * devuelve `error`, que se reintenta solo una vez y ofrece «Reintentar».
 */
export type PublicStatus = 'cargando' | 'lista' | 'no_encontrada' | 'error'

export interface PublicContentState {
  status: PublicStatus
  /** Mensaje real del fallo, solo cuando `status` es `error`. */
  message?: string
  retry: () => void
}

const RETRY_DELAY = 1200

/**
 * Descarga una landing publicada (y su formulario y marca) para un visitante
 * anónimo, y la vuelca en la caché para que las vistas la lean igual que
 * cuando hay sesión iniciada.
 */
export function usePublicLanding(slug: string): PublicContentState {
  const [status, setStatus] = useState<PublicStatus>('cargando')
  const [message, setMessage] = useState<string>()
  const [attempt, setAttempt] = useState(0)

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  useEffect(() => {
    let active = true
    let timer: number | undefined
    let autoRetried = false
    setStatus('cargando')
    setMessage(undefined)

    async function load(): Promise<void> {
      // Instrumentación: separa el viaje de red de la propia aplicación.
      mark('landing.inicio')
      measure('1. arranque de la app hasta pedir la landing')
      const landing = await timed('2. consulta de la landing (Supabase)', () =>
        fetchPublicLanding(slug),
      )
      measure('3. landing lista para renderizar', 'landing.inicio')
      if (!active) return
      if (!landing) {
        setStatus('no_encontrada')
        return
      }
      /*
       * Rendimiento: la landing se pinta EN CUANTO llega, sin esperar al
       * formulario ni a la marca. Antes se aguardaban las tres consultas antes
       * de salir de «cargando», así que el visitante veía el spinner durante
       * dos viajes de red aunque el hero (logo, titular, subtítulo, miniatura y
       * CTA) ya estuviera disponible. El formulario está por debajo del pliegue
       * y la marca solo es un respaldo del logotipo, que además viaja ya
       * resuelto dentro de la propia landing.
       */
      mergePublic({ landings: [landing] })
      setStatus('lista')

      // Complementarios: si fallan, la landing se muestra igualmente en lugar
      // de convertirse en un falso 404.
      const [form, organization] = await Promise.all([
        landing.formId
          ? timed('4. consulta del formulario (Supabase)', () =>
              fetchPublicFormById(landing.formId as string),
            ).catch(() => null)
          : null,
        timed('5. consulta de la marca (Supabase)', () =>
          fetchPublicOrganization(landing.organizationId),
        ).catch(() => null),
      ])
      if (!active) return
      mergePublic({
        forms: form ? [form] : [],
        organizations: organization ? [organization] : [],
      })
      measure('6. contenido complementario listo', 'landing.inicio')
    }

    function fail(error: unknown): void {
      if (!active) return
      if (!autoRetried) {
        // Reintento único: cubre la red inestable del primer toque en móvil.
        autoRetried = true
        timer = window.setTimeout(() => {
          load().catch(fail)
        }, RETRY_DELAY)
        return
      }
      setMessage(error instanceof Error ? error.message : undefined)
      setStatus('error')
    }

    load().catch(fail)

    return () => {
      active = false
      if (timer) window.clearTimeout(timer)
    }
  }, [slug, attempt])

  return { status, message, retry }
}

/** Descarga un formulario público activo por su slug. */
export function usePublicForm(slug: string): PublicContentState {
  const [status, setStatus] = useState<PublicStatus>('cargando')
  const [message, setMessage] = useState<string>()
  const [attempt, setAttempt] = useState(0)

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  useEffect(() => {
    let active = true
    let timer: number | undefined
    let autoRetried = false
    setStatus('cargando')
    setMessage(undefined)

    async function load(): Promise<void> {
      const form = await fetchPublicForm(slug)
      if (!active) return
      if (!form) {
        setStatus('no_encontrada')
        return
      }
      const organization = await fetchPublicOrganization(
        form.organizationId,
      ).catch(() => null)
      if (!active) return
      mergePublic({
        forms: [form],
        organizations: organization ? [organization] : [],
      })
      setStatus('lista')
    }

    function fail(error: unknown): void {
      if (!active) return
      if (!autoRetried) {
        autoRetried = true
        timer = window.setTimeout(() => {
          load().catch(fail)
        }, RETRY_DELAY)
        return
      }
      setMessage(error instanceof Error ? error.message : undefined)
      setStatus('error')
    }

    load().catch(fail)

    return () => {
      active = false
      if (timer) window.clearTimeout(timer)
    }
  }, [slug, attempt])

  return { status, message, retry }
}
